// src/pages/KarigarManagement/Job/components/ProductSelector.jsx

import React from "react";
import { Button } from "@/components/ui-v2/button";
import { Badge } from "@/components/ui-v2/badge";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui-v2/card";
import {
  ShoppingBag,
  Package,
  Gem,
  Users,
  Clock,
  Settings,
  Image as ImageIcon,
  Info,
  X,
} from "lucide-react";
import { cn } from "@/lib/utils";

/**
 * Product selector card component
 * Used in Step 2 to select a product for the job
 */
const ProductSelector = ({
  selectedProduct,
  onOpenDialog,
  onClear,
  productImageUrls = [],
  removedProductImageIndexes = new Set(),
  stones = [],
  suggestedKarigars = [],
  disabled = false,
}) => {
  const activeImageCount = productImageUrls.length - removedProductImageIndexes.size;

  return (
    <Card className="border-purple-200 bg-purple-50/30">
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <ShoppingBag className="h-5 w-5 text-purple-600" />
          Select Product (Optional)
        </CardTitle>
        <CardDescription>
          Select a product to auto-fill specifications, images, and suggested
          karigars
        </CardDescription>
      </CardHeader>
      <CardContent>
        {selectedProduct ? (
          <div className="space-y-4">
            {/* Selected Product Display */}
            <div className="flex items-center justify-between p-4 border rounded-lg bg-white border-purple-200">
              <div className="flex items-center gap-4">
                {/* Product Image */}
                {selectedProduct.media?.[0]?.url ? (
                  <img
                    src={selectedProduct.media[0].url}
                    alt={selectedProduct.name}
                    className="h-20 w-20 rounded-lg object-cover border"
                  />
                ) : (
                  <div className="h-20 w-20 rounded-lg bg-purple-100 flex items-center justify-center">
                    <Package className="h-10 w-10 text-purple-400" />
                  </div>
                )}
                <div>
                  <p className="font-semibold text-lg text-purple-900">
                    {selectedProduct.name}
                  </p>
                  <p className="text-sm text-purple-600">
                    SKU: {selectedProduct.sku || selectedProduct.productId}
                  </p>
                  <div className="flex items-center gap-2 mt-2 flex-wrap">
                    <Badge variant="outline" className="text-xs bg-white">
                      {selectedProduct.type}
                    </Badge>
                    {selectedProduct.metalDetails?.purity && (
                      <Badge variant="outline" className="text-xs bg-white">
                        {selectedProduct.metalDetails.purity}
                      </Badge>
                    )}
                    {selectedProduct.netWeight && (
                      <Badge variant="outline" className="text-xs bg-white">
                        {selectedProduct.netWeight}g
                      </Badge>
                    )}
                    {selectedProduct.hasStones && (
                      <Badge variant="outline" className="text-xs bg-white">
                        <Gem className="h-3 w-3 mr-1" />
                        Has Stones
                      </Badge>
                    )}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={onOpenDialog}
                  disabled={disabled}
                >
                  Change
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={onClear}
                  disabled={disabled}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            </div>

            {/* Auto-populated Summary */}
            <div className="p-3 bg-purple-100/50 rounded-lg border border-purple-200">
              <div className="flex items-center gap-2 mb-2">
                <Info className="h-4 w-4 text-purple-600" />
                <p className="text-sm font-medium text-purple-800">
                  Auto-populated from product:
                </p>
              </div>
              <div className="flex flex-wrap gap-2">
                <Badge variant="secondary" className="text-xs">
                  <Settings className="h-3 w-3 mr-1" />
                  Specifications
                </Badge>
                {productImageUrls.length > 0 && (
                  <Badge
                    variant="secondary"
                    className={cn(
                      "text-xs",
                      removedProductImageIndexes.size > 0 &&
                        "bg-orange-100 text-orange-700"
                    )}
                  >
                    <ImageIcon className="h-3 w-3 mr-1" />
                    {activeImageCount}/{productImageUrls.length} Images
                  </Badge>
                )}
                {stones.length > 0 && (
                  <Badge variant="secondary" className="text-xs">
                    <Gem className="h-3 w-3 mr-1" />
                    {stones.length} Stone(s)
                  </Badge>
                )}
                {suggestedKarigars.length > 0 && (
                  <Badge variant="secondary" className="text-xs">
                    <Users className="h-3 w-3 mr-1" />
                    {suggestedKarigars.length} Suggested Karigar(s)
                  </Badge>
                )}
                {selectedProduct.manufacturing?.estimatedMakingTime?.default && (
                  <Badge variant="secondary" className="text-xs">
                    <Clock className="h-3 w-3 mr-1" />
                    Est.{" "}
                    {selectedProduct.manufacturing.estimatedMakingTime.default}hrs
                  </Badge>
                )}
              </div>
            </div>
          </div>
        ) : (
          <Button
            type="button"
            variant="outline"
            className="w-full h-24 border-dashed border-purple-300 hover:border-purple-400 hover:bg-purple-50"
            onClick={onOpenDialog}
            disabled={disabled}
          >
            <div className="flex flex-col items-center gap-2">
              <ShoppingBag className="h-8 w-8 text-purple-400" />
              <span className="text-sm text-purple-600">
                Click to Select Product & Auto-fill Details
              </span>
            </div>
          </Button>
        )}
      </CardContent>
    </Card>
  );
};

export default ProductSelector;
